import React from 'react';
import './hdd.css';
import { Button, NumericInput } from '@blueprintjs/core';
import StringSelect from '../stringselect';
import ThemeContext from '../themecontext';
import SolutionPanel from '../solutionpanel';
import MathJax from 'react-mathjax';
import _ from 'lodash';

export default class HDD extends React.Component {

    constructor(props) {
        super(props);
        this.state = {  
                        seekTime: 4.5,
                        commandProcessing: 0.05,
                        RPM: 7200,
                        interfaceSpeed: 153600,
                        surfaces: 4,
                        zones: 2,
                        tracksInner: 120000,
                        sectorsInner: 2500,
                        taskZone: 1,
                        taskBlockSize: 8,
                        sectorSize: 512,
                        error: ""
                    };
        this.checkParamValidity = this.checkParamValidity.bind(this);
        this.generateParams = this.generateParams.bind(this);
    }

    componentDidMount() {
        this.generateParams();
    }

    generateParams() {
        const zones = _.random(2, 4);
        const RPM = _.sample([3000, 3125, 3750, 4000, 4800, 5000, 6000, 6250, 7500, 8000, 9375, 10000, 12000]);
        const sectorsInner = _.sample([200, 400, 500, 800, 1000, 1250, 1600, 2000]);
        const sectorSize = _.sample([512, 512, 512, 512, 4096]);
        const transferTimeMax = Math.min((60000 / RPM) / (2 * sectorsInner), sectorSize / (50*1024*1024)); // one sector transfer time
        const transferTimeMin = sectorSize / (600*1024*1024); // one sector transfer time
        const transferTimeGoal = _.random(transferTimeMin, transferTimeMax);
        let speed = 50 * 1024*1024;
        for (let i=1; i<=600; i++) {
            const trySpeed = i * 1024*1024;
            if (Math.abs(sectorSize / trySpeed - transferTimeGoal) < Math.abs(sectorSize / speed - transferTimeGoal))
                speed = trySpeed;
        }
        this.setState({
            seekTime: +_.random(4.0, 16.0).toFixed(1),
            commandProcessing: _.random(1, 100) / 1000.0,
            RPM: RPM,
            interfaceSpeed: speed,
            surfaces: 2*_.random(1, 5),
            zones: zones,
            tracksInner: _.random(6000, 300000/zones)*10,
            sectorsInner: sectorsInner,
            taskZone: _.random(1, zones),
            taskBlockSize: _.sample([8, 32768]), //_.random(8, 32768),
            sectorSize: sectorSize
        });
    }

    checkParamValidity() {
        const transferTime = this.state.sectorSize / this.state.interfaceSpeed;
        const fastestReadTime = 60000 / this.state.RPM / (2 * this.state.sectorsInner);
        if (transferTime > fastestReadTime)
            this.setState({error: `Átlapolt adatátvitel nem lehetséges! (Leggyorsabb leolvasási idő: ${fastestReadTime} sec < adatátviteli idő: ${transferTime} sec!)`});
        else
            this.setState({error: ""});
    }

    static contextType = ThemeContext;

    render() {
        const sectorSizesStr = _.range(1, this.state.zones+1).map(i => String(Math.round(this.state.sectorsInner + this.state.sectorsInner * (i-1) / (this.state.zones - 1))) + "\\cdot " + String(this.state.tracksInner)).join(" + ");
        const capacity = this.state.surfaces*this.state.tracksInner*this.state.sectorSize*_.range(1, this.state.zones+1).map(i => Math.round(this.state.sectorsInner + this.state.sectorsInner * (i-1) / (this.state.zones - 1))).reduce((total, num) => total+num, 0)
        const rotTime = 60000/this.state.RPM;
        const taskSectorNum = Math.round(this.state.sectorsInner + this.state.sectorsInner * (this.state.taskZone-1) / (this.state.zones - 1));
        const sectorTime = rotTime / taskSectorNum;
        const transferTime = 1000.0 * this.state.sectorSize / this.state.interfaceSpeed;
        const serviceTime = this.state.commandProcessing + this.state.seekTime + rotTime/2.0 + sectorTime*this.state.taskBlockSize + transferTime;
        const iops = 1000 / serviceTime;
        const throughput = iops * this.state.taskBlockSize * this.state.sectorSize;
        const prec = 10000;
        return (            
            <> 
                <div className="title-area">
                    <h1>Merevlemezek</h1>
                    <h2>Tárolási kapacitás és teljesítményjellemzők</h2>
                </div>
                <div className="demo-emph-row">
                    <div className="hdd-param-panel">
                        <h3>Paraméterek:</h3>
                        <p className="hdd-param-title">Egy merevlemez az alábbi paraméterekkel rendelkezik.</p>                        
                        <div className="hdd-param-grid">
                            <div className="hdd-param-label">Adathordozó felületek száma: </div>
                            <div className="hdd-param-row">
                                <NumericInput 
                                    className="hdd-param-numeric"
                                    fill="true"
                                    value={this.state.surfaces}
                                    onValueChange={val => this.setState({surfaces: val})}
                                    min={2}
                                    max={10}
                                />
                            </div>
                            <div className="hdd-param-label">ZBR zónák száma: </div>
                            <div className="hdd-param-row">
                                <NumericInput 
                                    className="hdd-param-numeric"
                                    fill="true"
                                    value={this.state.zones}
                                    onValueChange={val => this.setState({zones: val})}
                                    min={2}
                                    max={4}
                                />
                            </div>                            
                            <div className="hdd-param-label">Sávok száma, adathordozó felületenként: </div>
                            <div className="hdd-param-row">
                                minden zónában &nbsp;
                                <NumericInput 
                                    className="hdd-param-numeric"
                                    fill="true"
                                    value={this.state.tracksInner}
                                    onValueChange={val => this.setState({tracksInner: val})}
                                    min={30000}
                                    max={3000000}
                                /> sáv
                            </div>
                            <div className="hdd-param-label">Szektorok száma sávonként: </div>
                            <div className="hdd-param-row">
                                az 1. (legbelső) zónában &nbsp;
                                <NumericInput 
                                    className="hdd-param-numeric"
                                    fill="true"
                                    value={this.state.sectorsInner}
                                    onValueChange={val => this.setState({sectorsInner: val})}
                                    min={100}
                                    max={3000}
                                /> a többi zónában: {this.state.zones && _.range(2, this.state.zones+1).map(i => String(Math.round(this.state.sectorsInner + this.state.sectorsInner * (i-1) / (this.state.zones - 1)))).join(", ")}
                            </div>
                            <div className="hdd-param-label">Egy szektor mérete: </div>
                            <div className="hdd-param-row">
                                <StringSelect
                                        className="hdd-param-numeric"
                                        items={["512", "4096"]}
                                        initial={String(this.state.sectorSize)}
                                        onChange={val => this.setState({sectorSize: +val})}
                                />
                                &nbsp; byte
                            </div>
                            <div className="hdd-param-label">Parancsfeldolgozási késleltetés: </div>
                            <div className="hdd-param-row">
                                <NumericInput 
                                    className="hdd-param-numeric"
                                    fill="true"
                                    value={this.state.commandProcessing}
                                    onValueChange={val => this.setState({commandProcessing: val})}
                                    min={0.001}                                    
                                    max={0.1}
                                    minorStepSize={0.001}
                                    stepSize={0.001}
                                /> ms
                            </div>
                            <div className="hdd-param-label">Átlagos seek idő: </div>
                            <div className="hdd-param-row">
                                <NumericInput 
                                    className="hdd-param-numeric"
                                    fill="true"
                                    value={this.state.seekTime}
                                    onValueChange={val => this.setState({seekTime: val})}
                                    min={1}                                    
                                    max={100}
                                    minorStepSize={0.1}
                                    stepSize={0.1}
                                /> ms
                            </div>
                            <div className="hdd-param-label">Forgási sebesség: </div>
                            <div className="hdd-param-row">
                                <NumericInput 
                                    className="hdd-param-numeric"
                                    fill="true"
                                    value={this.state.RPM}
                                    onValueChange={val => this.setState({RPM: val})}
                                    min={1000}                                    
                                    max={20000}
                                /> RPM (fordulat / perc)
                            </div>
                            <div className="hdd-param-label">Interfész átviteli sebessége: </div>
                            <div className="hdd-param-row">
                                <NumericInput 
                                    className="hdd-param-numeric"
                                    fill="true"
                                    value={this.state.interfaceSpeed}
                                    onValueChange={val => this.setState({interfaceSpeed: val})}
                                /> byte/s
                            </div>                            
                        </div>
                        <Button 
                            icon="refresh"
                            text="Random generálás"
                            className="hdd-generate-button"
                            onClick={this.generateParams} 
                        />

                        <div>Tegyük fel, hogy a HDD-hez érkező kérések <div className="hdd-param-numeric-inline">
                                <NumericInput                                     
                                    fill="true"
                                    value={this.state.taskBlockSize}
                                    onValueChange={val => this.setState({taskBlockSize: val})}
                                />
                            </div> szektorra ({this.state.taskBlockSize*this.state.sectorSize} byte-ra) vonatkoznak, 
                            melyek a(z) <div className="hdd-param-numeric-inline-short">
                                <NumericInput                                     
                                    fill="true"
                                    value={this.state.taskZone}
                                    min={1}
                                    max={this.state.zones}
                                    onValueChange={val => this.setState({taskZone: val})}
                                />
                            </div>. zónában helyezkednek el.</div>
                    </div>
                </div>
                <div>
                    <h3>1. Feladat</h3>
                    <p> Mekkora a merevlemez teljes tárolási kapacitása, byte-ban mérve? </p>
                </div>
                <div>
                    <SolutionPanel title="Megoldás mutatása">                        
                        <div className="demo-emph-row">
                        <MathJax.Provider>
                            <div>
                                Összeszorozzuk az adathordozó rétegek számát, majd minden zónára a szektorok számát, végül megszorozzuk a szektorok méretével:
                                <div className="demo-solution-value"><MathJax.Node formula={`${this.state.surfaces}\\cdot(${sectorSizesStr})\\cdot ${this.state.sectorSize} = ${capacity} \\,\\text{byte} \\approx ${Math.round(capacity/1024/1024/1024)} \\,\\text{GB}`}/></div>
                            </div>
                        </MathJax.Provider>
                        </div>
                    </SolutionPanel>
                </div>
                <div>
                    <h3>2. Feladat</h3>
                    <p> Számítsuk ki a fenti kérések átlagos kiszolgálási idejét (4 tizedesjegyre kerekítve)!</p>
                </div>
                <div>
                    <SolutionPanel title="Megoldás mutatása"> 
                        <div className="demo-emph-row">
                        <MathJax.Provider><div className="hdd-mathjaxdiv">
                        Először számítsuk ki a kiszolgálási idő összetevőit:
                        <ul>
                        <li>Parancsfeldolgozási késleltetés: <span className="demo-solution-value"><MathJax.Node inline formula={`T_{proc}=${this.state.commandProcessing} \\,\\text{ms}`}/></span></li>
                        <li>Átlagos seek idő: <span className="demo-solution-value"><MathJax.Node inline formula={`T_{seek}=${this.state.seekTime} \\,\\text{ms}`}/></span></li>
                        <li>Körülfordulási idő: 
                            <div className="demo-solution-value"><MathJax.Node formula={`T_{revolution} = \\frac{60000 \\,\\text{ms/min}}{${this.state.RPM}\\,\\text{RPM}} = ${Math.round(rotTime*prec)/prec}\\,\\text{ms}`}/></div>
                        </li>
                        <li>A körülfordulási idő alapján a forgási késleltetés: 
                            <div className="demo-solution-value"><MathJax.Node formula={`T_{rot} = \\frac{1}{2}\\cdot ${Math.round(rotTime*prec)/prec} \\,\\text{ms} = ${Math.round(rotTime/2.0*prec)/prec}\\,\\text{ms}`}/></div>                            
                        </li>
                        <li>Egyelten szektor leolvasási ideje az adathordozóról:
                            <div className="demo-solution-value"><MathJax.Node formula={`T_{read} = \\frac{${Math.round(rotTime*prec)/prec}\\,\\text{ms}}{${taskSectorNum}} = ${Math.round(sectorTime*prec)/prec}\\,\\text{ms}`}/></div>                            
                        </li>
                        <li>Egyelten szektor átviteli ideje az adatátviteli interfészen:
                            <div className="demo-solution-value"><MathJax.Node formula={`T_{transfer} = \\frac{${this.state.sectorSize}\\,\\text{byte}}{${this.state.interfaceSpeed}\\,\\text{byte/sec}}\\cdot 1000\\frac{\\text{sec}}{\\text{ms}} = ${Math.round(transferTime*prec)/prec}\\,\\text{ms}`}/></div>                            
                        </li>
                        </ul>
                        Ezek alapján az adott számú szektor átlagos kiszolgálási ideje (a szektorleolvasási és az interfészátviteli idő átlapolhatóságát figyelembe véve):
                        <div className="demo-solution-value"><MathJax.Node formula={`T_{service} = T_{proc} + T_{seek} + T_{rot} + ${this.state.taskBlockSize}\\cdot T_{read} + T_{transfer} = ${Math.round(serviceTime*prec)/prec} \\,\\text{ms}`}/></div>                            
                       </div></MathJax.Provider>
                       </div>
                    </SolutionPanel>
                </div>                
                <div>
                    <h3>3. Feladat</h3>
                    <p> Számítsuk ki a fenti kérések mellett az átvitelt (throughput-ot) 4 tizedesjegyre kerekítve!</p>
                </div>
                <div>
                    <SolutionPanel title="Megoldás mutatása"> 
                        <div className="demo-emph-row">
                        <MathJax.Provider><div className="hdd-mathjaxdiv">
                        Elsőként kiszámítjuk, hogy hány kérés kiszolgálása fér bele egy másodpercbe, ez lesz az IOPS:
                        <div className="demo-solution-value"><MathJax.Node formula={`\\text{IOPS} = \\frac{1000 \\,\\text{ms/sec}}{T_{service} \\,\\text{ms/kérés}} = \\frac{1000}{${Math.round(serviceTime*prec)/prec}} = ${Math.round(iops*prec)/prec} \\,\\text{kérés/sec}`}/></div>
                        Az átvitel (throughput) pedig nem más, mint ennek a kifejezése byte/sec mértékegységben mérve, azaz meg kell szorozunk a kérések hosszával.
                        A kérések {this.state.taskBlockSize} szektorra vonatkoznak, ami {this.state.taskBlockSize*this.state.sectorSize} byte-nak felel meg. Az átvitel ennek megfelelően:
                        <div className="demo-solution-value"><MathJax.Node formula={`\\text{Throughput} = \\text{IOPS}\\,\\text{kérés/sec}\\cdot ${this.state.taskBlockSize*this.state.sectorSize}\\,\\text{byte/kérés} = ${Math.round(throughput*prec)/prec} \\,\\text{byte/sec}`}/></div>
                        </div></MathJax.Provider>                        
                       </div>
                    </SolutionPanel>
                </div>                
            </>            
        );
    }
}